import {
  Box,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Switch,
  useToast
} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory } from "react-router-dom";
import { Column } from "react-table";
import { FaTrash } from "react-icons/fa";

import { Header } from "src/components/shared/Header";
import {
  Table,
  ITableInstance
} from "src/components/table";
import { Text } from "src/components/text";
import {
  User,
  useUsersApi
} from "src/api";
import { Avatar } from "src/components/avatar";
import {
  DestructiveButton,
  SolidButton
} from "src/components/button";
import { useMissionsApi } from "src/api/missions/missions.api";
import { theme } from "src/components/theme";

interface UsersAdminTableData {
  _id: User["_id"]
  name: User["name"]
  email: User["email"]
  admin: User["admin"]
  active: User["active"]
  missionId?: User["missionId"]
}

export const UsersAdminPage: React.FC = () => {
  const history = useHistory();
  const toast = useToast();
  const tableRef = useRef<ITableInstance>();
  const [ usersRes, usersReq ] = useUsersApi("GET_USERS");
  const [ deleteUserRes, deleteUserReq ] = useUsersApi("DELETE_USER");
  const [ updateUserRes, updateUserReq ] = useUsersApi("UPDATE_USER");
  const [ missionsRes, missionsReq ] = useMissionsApi("GET_MISSIONS");
  const [ usersTableData, setUsersTableData ] = useState<UsersAdminTableData[]>([]);
  const [ userIdToDelete, setUserIdToDelete ] = useState<string | null>(null);

  const handleDelete = useCallback((userId: string) => {
    setUserIdToDelete(null);
    deleteUserReq({ id: userId });
  }, [ deleteUserReq ]);

  useEffect(() => {
    missionsReq({});
  }, [ missionsReq ]);

  const missions = useMemo(() => {
    const results = missionsRes.data?.results || [];

    return [ ...results ].sort((left, right) => left.name.localeCompare(right.name, undefined, {
      numeric: true,
      sensitivity: "base"
    }));
  }, [ missionsRes.data ]);

  const saveUser = useCallback((id: string, patch: { missionId?: string; active?: boolean }) => {
    updateUserReq({
      id,
      ...patch
    });
  }, [ updateUserReq ]);

  const columns = useMemo(
    (): Array<Column<UsersAdminTableData>> => [
      {
        Header: "Name",
        accessor: "name",
        Cell: props => (
          <Avatar name={props.value || ""} />
        )
      },
      {
        Header: "Email",
        accessor: "email"
      },
      {
        Header: "User Type",
        accessor: "admin",
        Cell: props => (
          <div>
            {props.value ? "Admin" : "Regular user"}
          </div>
        )
      },
      {
        Header: "Mission",
        accessor: "missionId",
        Cell: props => (
          <Select
            size="sm"
            value={props.value || ""}
            onChange={event => saveUser(props.row.original._id, { missionId: event.target.value })}
          >
            {missions.map(mission => (
              <option
                key={mission._id}
                value={mission._id}
              >
                {mission.name}
              </option>
            ))}
          </Select>
        )
      },
      {
        Header: "Active",
        accessor: "active",
        Cell: props => (
          <Switch
            isChecked={props.value !== false}
            onChange={event => saveUser(props.row.original._id, { active: event.target.checked })}
          />
        )
      },
      {
        Header: "Actions",
        accessor: "_id",
        Cell: props => (
          <Icon
            cursor="pointer"
            _hover={{ color: theme.colors.warn }}
            onClick={() => setUserIdToDelete(props.value)}
            w={18}
            h={18}
            as={FaTrash}
          />

        )
      }
    ],
    [ missions, saveUser ]
  );

  const handleFetch = useCallback((pageIndex: number, pageSize: number) => {
    const skip = pageIndex * pageSize;

    usersReq({
      skip,
      limit: pageSize
    });
  }, [ usersReq ]);

  const pageCount = useMemo(() =>
    usersRes.data ?
      usersRes.data?.total < 10 ? 1 : Math.ceil(usersRes.data?.total / 10) :
      1,
  [ usersRes ]);

  useEffect(() => {
    if (usersRes.data) {
      setUsersTableData(usersRes.data.results);
    }

    if (usersRes.error) {
      toast({
        status: "error",
        title: "Error getting users"
      });
    }
  }, [ usersRes, toast ]);

  useEffect(() => {
    if (deleteUserRes.data) {
      toast({
        status: "success",
        title: "User deleted succesfully."
      });
      // Reload the table
      tableRef.current?.reloadTable();
    }

    if (deleteUserRes.error) {
      toast({
        status: "error",
        title: "Error deleting user",
        description: deleteUserRes.error.details
      });
    }
  }, [
    deleteUserRes,
    toast,
    handleFetch
  ]);

  useEffect(() => {
    if (updateUserRes.data) {
      setUsersTableData(current => current.map(user => (
        user._id === updateUserRes.data?._id ? {
          ...user,
          ...updateUserRes.data
        } : user
      )));

      toast({
        status: "success",
        title: "User updated"
      });
    }

    if (updateUserRes.error) {
      toast({
        status: "error",
        title: "Could not update user",
        description: updateUserRes.error.details
      });
    }
  }, [ updateUserRes, toast ]);

  return (
    <Box p={10}>

      <Modal
        isOpen={!!userIdToDelete}
        onClose={() => setUserIdToDelete(null)}
      >
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>
            Delete user
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <Text>
              Are you sure you want to delete this user?
            </Text>
          </ModalBody>

          <ModalFooter>
            <SolidButton
              mr={3}
              onClick={() => setUserIdToDelete(null)}
            >
              Close
            </SolidButton>

            <DestructiveButton
              mr={3}
              onClick={() => handleDelete(userIdToDelete as string)}
            >
              Delete user
            </DestructiveButton>

          </ModalFooter>
        </ModalContent>
      </Modal>

      <Header />

      <Box paddingY={10}>
        <Text as="h1">
          All Users
        </Text>

        <Box
          paddingY={4}
          display="flex"
          justifyContent="space-between"
        >
          <Text as="p">
            View and manage users. This is an admin-only page.
          </Text>

          <SolidButton
            onClick={() => history.push("/users/create")}
          >
            Create user
          </SolidButton>
        </Box>

        <Table
          ref={tableRef}
          columns={columns}
          fetchData={handleFetch}
          loading={usersRes.pending}
          pageCount={pageCount}
          data={usersTableData || []}
        />

      </Box>

    </Box>
  );
};
